import { Controller } from "@hotwired/stimulus"

// The line motion of Samuel's demo (classes in shop_cart.css): a removed line collapses before the
// stream replaces #shop_cart, a new line fades in, a line an « Ajouter » raised (a Coffret added
// again) flashes, and the page scrolls to the line it touched; « Dans le panier » scrolls to its
// add-on's line. Turbo 7.3 has no morphing, so the stream still swaps the whole block: lines are
// compared by their stable ids (dom_id) and data-quantity, and only the changed one moves.
// Forms say what they do with data-cart-motion="add" or "leave". Wired outside #shop_cart (as
// shop-focus); the stream element lives in <body>, hence @document. No optional chaining (Webpacker 5).
const LEAVE_MS = 300

export default class extends Controller {
  connect() {
    this.adding = false
    this.leaving = null
    this.leavingCard = null
  }

  disconnect() {
    clearTimeout(this.leaveTimer)
  }

  // submit (capture): before Turbo sends the form.
  track(event) {
    const motion = event.target.getAttribute("data-cart-motion")
    this.adding = motion === "add"
    if (motion === "leave") this.collapse(event.target.closest(".card"))
  }

  // A request that failed (no stream comes back) gives the collapsed line back.
  settle(event) {
    if (event.detail.success || !this.leavingCard) return

    this.leavingCard.classList.remove("leaving")
    this.leavingCard.style.height = ""
    this.leavingCard = null
    this.leaving = null
  }

  wrap(event) {
    const stream = event.target
    if (!stream || stream.getAttribute("target") !== "shop_cart") return

    const before = this.quantities()
    const adding = this.adding
    const leaving = this.leaving
    this.adding = false
    this.leaving = null
    this.leavingCard = null
    const originalRender = event.detail.render

    event.detail.render = async (streamElement) => {
      if (leaving) await leaving
      await originalRender(streamElement)
      this.animate(before, adding)
    }
  }

  // « Dans le panier »: a plain link to the line, made smooth.
  reveal(event) {
    const card = document.getElementById(event.currentTarget.hash.slice(1))
    if (!card) return

    event.preventDefault()
    this.flash(card)
    this.scrollTo(card)
  }

  collapse(card) {
    if (!card || this.reducedMotion()) return

    card.style.height = card.offsetHeight + "px"
    card.getBoundingClientRect() // commit the height so the collapse transitions from it
    card.classList.add("leaving")
    card.style.height = "0px"
    this.leavingCard = card
    this.leaving = new Promise((resolve) => { this.leaveTimer = setTimeout(resolve, LEAVE_MS) })
  }

  animate(before, adding) {
    let touched = null
    this.cards().forEach((card) => {
      if (!Object.prototype.hasOwnProperty.call(before, card.id)) {
        if (!this.reducedMotion()) card.classList.add("enter")
        touched = card
      } else if (adding && Number(card.dataset.quantity) > before[card.id]) {
        this.flash(card)
        touched = card
      }
    })
    if (adding && touched && !this.inView(touched)) this.scrollTo(touched)
  }

  flash(card) {
    if (this.reducedMotion()) return

    card.classList.remove("flash")
    card.getBoundingClientRect() // restart the animation on a second click
    card.classList.add("flash")
  }

  scrollTo(card) {
    card.scrollIntoView({ behavior: this.reducedMotion() ? "auto" : "smooth", block: "center" })
  }

  // Fully visible between the sticky top bar and the phone's fixed pay bar.
  inView(card) {
    const rect = card.getBoundingClientRect()
    const bar = document.querySelector(".topnav")
    const mbar = document.querySelector(".mbar")
    const top = bar ? bar.getBoundingClientRect().bottom : 0
    const bottom = mbar && getComputedStyle(mbar).display !== "none" ? mbar.getBoundingClientRect().top : window.innerHeight
    return rect.top >= top && rect.bottom <= bottom
  }

  cards() {
    return Array.from(document.querySelectorAll("#items > .card[id]"))
  }

  quantities() {
    const map = {}
    this.cards().forEach((card) => { map[card.id] = Number(card.dataset.quantity) })
    return map
  }

  reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
